<template>
  <b-container class="m-0 p-0 mb-2" fluid>
    <b-row class="align-items-center mb-3">
      <b-col cols="8">
        <strong>
          {{ $t("teams.extra_data_kv_label", "Team extra data") }}
        </strong>
        <b-form-text class="font-weight-bold">
          <b-icon-question-circle-fill />
          {{
            $t(
              "teams.extra_data_kv_help",
              "Custom key/value pairs stored on this team. Keys may contain letters, numbers, hyphens and underscores (max 64 chars). Values max 2048 chars. Up to 100 entries."
            )
          }}
        </b-form-text>
      </b-col>
      <b-col class="d-flex justify-content-end" cols="4">
        <button
          type="button"
          class="bttn bttn--sm bttn--orange"
          @click="add"
          :disabled="disabled || atMaxEntries"
        >
          {{ $t("teams.extra_data_kv_add", "Add row") }}
        </button>
      </b-col>
    </b-row>
    <b-row v-if="atMaxEntries">
      <b-col>
        <small class="text-danger font-weight-bold">
          <b-icon-exclamation-circle-fill />
          {{
            $t("teams.extra_data_kv_max_entries_error", `Maximum ${MAX_ENTRIES} entries allowed.`)
          }}
        </small>
      </b-col>
    </b-row>
    <b-row v-for="(row, i) in rows" :key="i" class="mb-2">
      <b-col cols="12" lg="5">
        <b-form-input
          :value="row.key"
          @input="updateKey(i, $event)"
          :placeholder="$t('teams.extra_data_kv_key_placeholder', 'key')"
          :state="keyState(i)"
          :disabled="disabled"
          form="team-add"
          maxlength="64"
        />
        <b-form-invalid-feedback v-if="keyError(i)">
          {{ keyError(i) }}
        </b-form-invalid-feedback>
      </b-col>
      <b-col cols="12" lg="6">
        <b-form-input
          :value="row.value"
          @input="updateValue(i, $event)"
          :placeholder="$t('teams.extra_data_kv_value_placeholder', 'value')"
          :state="valueState(i)"
          :disabled="disabled"
          form="team-add"
          maxlength="2048"
        />
        <b-form-invalid-feedback v-if="valueError(i)">
          {{ valueError(i) }}
        </b-form-invalid-feedback>
      </b-col>
      <b-col cols="12" lg="1" class="d-flex align-items-start">
        <b-button
          @click="remove(i)"
          variant="outlined-secondary"
          class="bttn--inverted-blue"
          :disabled="disabled"
          :title="$t('teams.extra_data_kv_remove', 'Remove')"
        >
          <b-icon icon="trash-fill" />
        </b-button>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { defineComponent } from "vue";
import { BIconQuestionCircleFill, BIconExclamationCircleFill } from "bootstrap-vue";

const KEY_REGEX = /^[a-zA-Z0-9_-]{1,64}$/;
const MAX_VALUE_LEN = 2048;
const MAX_ENTRIES = 100;

export default defineComponent({
  components: {
    BIconQuestionCircleFill,
    BIconExclamationCircleFill,
  },

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    disabled: Boolean,
  },

  data() {
    return {
      MAX_ENTRIES,
    };
  },

  computed: {
    rows() {
      return Array.isArray(this.value) ? this.value : [];
    },
    atMaxEntries() {
      return this.rows.length >= MAX_ENTRIES;
    },
    duplicateKeys() {
      const seen = {};
      const dupes = {};
      this.rows.forEach((row, i) => {
        const key = (row.key || "").trim();
        if (!key) return;
        if (seen[key] !== undefined) {
          dupes[i] = true;
          dupes[seen[key]] = true;
        } else {
          seen[key] = i;
        }
      });
      return dupes;
    },
  },

  methods: {
    emit(rows) {
      this.$emit("input", rows);
    },

    add() {
      if (this.atMaxEntries) return;
      this.emit([...this.rows, { key: "", value: "" }]);
    },

    remove(i) {
      const copy = this.rows.slice();
      copy.splice(i, 1);
      this.emit(copy);
    },

    updateKey(i, newKey) {
      const copy = this.rows.slice();
      copy[i] = { ...copy[i], key: newKey };
      this.emit(copy);
    },

    updateValue(i, newValue) {
      const copy = this.rows.slice();
      copy[i] = { ...copy[i], value: newValue };
      this.emit(copy);
    },

    isRowEmpty(row) {
      return !(row?.key || "").trim() && !String(row?.value ?? "").length;
    },

    keyError(i) {
      const row = this.rows[i];
      // Fully-empty rows are silently dropped on save — don't nag the user.
      if (this.isRowEmpty(row)) return "";
      const key = (row?.key || "").trim();
      if (!key) {
        return this.$t("teams.extra_data_kv_key_required", "Key is required");
      }
      if (!KEY_REGEX.test(key)) {
        return this.$t(
          "teams.extra_data_kv_key_invalid",
          "Key must match [a-zA-Z0-9_-] and be 1-64 chars"
        );
      }
      if (this.duplicateKeys[i]) {
        return this.$t("teams.extra_data_kv_key_duplicate", "Key must be unique");
      }
      return "";
    },

    valueError(i) {
      const row = this.rows[i];
      if (this.isRowEmpty(row)) return "";
      const value = row?.value ?? "";
      if (String(value).length > MAX_VALUE_LEN) {
        return this.$t(
          "teams.extra_data_kv_value_too_long",
          `Value must be ${MAX_VALUE_LEN} chars or fewer`
        );
      }
      return "";
    },

    keyState(i) {
      return this.keyError(i) ? false : null;
    },

    valueState(i) {
      return this.valueError(i) ? false : null;
    },

    validate() {
      for (let i = 0; i < this.rows.length; i++) {
        if (this.keyError(i) || this.valueError(i)) return false;
      }
      return true;
    },
  },
});
</script>
